import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AddButton,
  Chip,
  ModalNewStaff,
  SearchInput,
  Table,
  TableItem,
} from "../../components";
import { ROLES } from "../../utils/constants";
import styles from "./StaffScreen.module.scss";
import debounce from "lodash.debounce";
import { ReactComponent as ChevronIcon } from "../../assets/icons/chevron_right.svg";
import { ORDER } from "../../components/Table/Table";
import { selectStaff } from "../../store/slices/staff/slice";
import { fetchStaff } from "../../store/slices/staff/asyncThunks";

const StaffScreen = () => {
  const dispatch = useDispatch();
  let page = +useLocation().search.split("=")[1];
  let navigate = useNavigate();

  const { data, perPage, total } = useSelector(selectStaff);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortOptions, setSortOptions] = useState({
    sortBy: "",
    order: ORDER.ASC,
  });
  const [selectedStaff, setSelectedStaff] = useState(null);

  const newStaffModal = useRef();

  const tableHeader = [
    {
      key: "firstName",
      label: "Name",
      sortable: true,
    },
    {
      key: "externalId",
      label: "External ID",
      sortable: true,
    },
    {
      key: "email",
      label: "Email",
      sortable: true,
    },
    {
      key: "role",
      label: "Role",
      sortable: true,
    },
    {
      key: "",
      label: "",
    },
  ];

  const setPage = async (page) => {
    setCurrentPage(page);
    navigate({
      pathname: "/staff",
      search: `?page=${page}`,
    });
  };

  const changeHandler = useCallback(
    (text) => {
      setSearch(text);
      dispatch(
        fetchStaff({
          page: Number(page),
          sortBy: sortOptions.sortBy,
          order: sortOptions.order,
          search: text,
        })
      );
    },
    [page, sortOptions, dispatch]
  );

  const debouncedChangeHandler = useMemo(
    () => debounce(changeHandler, 300),
    [changeHandler]
  );

  useEffect(() => {
    return () => {
      debouncedChangeHandler.cancel();
    };
  });

  useEffect(() => {
    setCurrentPage(page);
    if (page > 0) {
      dispatch(
        fetchStaff({
          page: Number(page),
          sortBy: sortOptions.sortBy,
          order: sortOptions.order,
          search: search,
        })
      );
    }
    //eslint-disable-next-line
  }, [page, sortOptions, dispatch]);

  return (
    <div className={styles.container}>
      <h3 className={styles.title}>Staff</h3>
      <div className={styles.header}>
        <SearchInput
          onChange={debouncedChangeHandler}
          placeholder="Search Staff"
        />
        <AddButton
          title="Add Staff"
          onClick={() => {
            setSelectedStaff(null);
            newStaffModal.current.show();
          }}
        />
      </div>
      <div className={styles.content}>
        <Table
          currentPage={currentPage}
          totalCount={total}
          pageSize={perPage}
          setPage={setPage}
          noDataContent="No Data"
          setSortOptions={setSortOptions}
          sortOptions={sortOptions}
          headerInformation={tableHeader}
        >
          {data.map((staff, index) => {
            return (
              <Fragment key={index}>
                <TableItem
                  data={
                    <div className={styles.nameContainer}>
                      <Chip
                        img={staff.profilePhoto}
                        firstName={staff.firstName}
                        lastName={staff.lastName}
                      />
                      <p className={styles.nameText}>
                        {staff.firstName + " " + staff.lastName}
                      </p>
                    </div>
                  }
                />
                <TableItem data={staff.externalId} />
                <TableItem data={staff.email} />
                <TableItem data={staff.role} />
                <TableItem
                  data={
                    <div
                      className={styles.iconContainer}
                      onClick={() => {
                        setSelectedStaff(staff);
                        newStaffModal.current.show();
                      }}
                    >
                      <ChevronIcon className={styles.icon} />
                    </div>
                  }
                />
              </Fragment>
            );
          })}
        </Table>
      </div>
      <ModalNewStaff
        staffType={ROLES.ADMIN}
        modalRef={newStaffModal}
        selectedStaff={selectedStaff}
      />
    </div>
  );
};

export default StaffScreen;
